import React, { useState, useEffect, useRef } from 'react';
import { C, R, SHADOW, fmt } from '../tokens.js';
import { D } from '../mock-data.js';
import { BkHdr, Chip, Badge, ImgOrEmoji, Card } from '../components/ui.jsx';
import { listSubscribableProducts, listNewProducts } from '../api/products.js';

const fullBranchFor = (branchId) => D.branches.grocery.find((b) => b.id === branchId) || D.branches.salons.find((b) => b.id === branchId);

const CATEGORY_FILTERS = ['All', 'Dairy', 'Bakery', 'Vegetables', 'Poultry'];
const CADENCE_FILTERS = ['All', 'Daily', 'Alt-day', 'Weekly'];

export const ProductCatalogScreen = ({ navigate, goBack, addToRoutineBasket, routineBasket, initialCategory }) => {
  const [category, setCategory] = useState(initialCategory || 'All');
  const [cadence, setCadence] = useState('All');
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newProducts, setNewProducts] = useState([]);
  const [justAdded, setJustAdded] = useState(null);
  const listRef = useRef(null);

  const inBasket = (productId, variantLabel) =>
    routineBasket?.items?.some((it) => it.productId === productId && it.variantLabel === variantLabel);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setCursor(null);
    const params = {
      category: category === 'All' ? undefined : category,
      cadence: cadence === 'All' ? undefined : cadence,
      limit: 20,
    };
    listSubscribableProducts(params).then((res) => {
      if (cancelled) return;
      setItems(res.items || []);
      setCursor(res.nextCursor || null);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [category, cadence]);

  useEffect(() => {
    listNewProducts({ limit: 8, sinceDays: 14 }).then(setNewProducts).catch(() => setNewProducts([]));
  }, []);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await listSubscribableProducts({
        category: category === 'All' ? undefined : category,
        cadence: cadence === 'All' ? undefined : cadence,
        cursor,
        limit: 20,
      });
      setItems((prev) => [...prev, ...(res.items || [])]);
      setCursor(res.nextCursor || null);
    } finally {
      setLoadingMore(false);
    }
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) loadMore();
  };

  const handleAdd = (p) => {
    const branch = { id: p.branchId, name: p.branchName, rating: p.branchRating };
    addToRoutineBasket(p, branch);
    setJustAdded(p.id + ':' + (p.variants?.[0]?.l || ''));
    setTimeout(() => setJustAdded(null), 1200);
  };

  return (
    <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg }}>
      <BkHdr
        title="Add to your routine"
        onBack={goBack}
        right={
          <button onClick={() => navigate('search', { initialCategory: category === 'All' ? undefined : category })}
            style={{ width: 40, height: 40, borderRadius: R.lg, background: C.white, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke={C.text} strokeWidth="1.6" /><path d="M11.5 11.5L14 14" stroke={C.text} strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        }
      />

      {/* Filter strips */}
      <div style={{ padding: '12px 16px 8px', background: C.bg, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', marginBottom: 8, paddingBottom: 2 }}>
          {CADENCE_FILTERS.map((f) => (
            <Chip key={f} active={cadence === f} onClick={() => setCadence(f)}>{f}</Chip>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
          {CATEGORY_FILTERS.map((f) => (
            <Chip key={f} active={category === f} onClick={() => setCategory(f)}>{f}</Chip>
          ))}
        </div>
      </div>

      {/* Body */}
      <div ref={listRef} onScroll={onScroll} style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 100px' }}>

        {/* New on Rekraft rail */}
        {newProducts.length >= 2 && (
          <div style={{ margin: '8px 0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: -0.1 }}>🆕 New on Rekraft</span>
              <Badge bg={C.warningBg} color={C.warning} sm>Last 14 days</Badge>
            </div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4, margin: '0 -4px', padding: '0 4px 4px' }}>
              {newProducts.map((p) => (
                <div key={p.id} onClick={() => navigate('product', { product: p, branch: { id: p.branchId, name: p.branchName, rating: p.branchRating } })}
                  style={{ width: 140, flexShrink: 0, background: C.white, borderRadius: R.xl, padding: 12, border: `1px solid ${C.border}`, cursor: 'pointer', boxShadow: SHADOW.card }}>
                  <div style={{ width: 60, height: 60, borderRadius: R.lg, background: '#e6f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, marginBottom: 8 }}>{p.em}</div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                  <p style={{ fontSize: 10, color: C.textMed, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.branchName}</p>
                  <p style={{ fontSize: 12, fontWeight: 800, color: C.primary }}>from {fmt(p.variants?.[0]?.p || 0)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product list */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: C.textMed, fontSize: 13 }}>Loading subscribable products…</div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
            <p style={{ fontSize: 15, fontWeight: 700, color: C.text }}>No subscribable products match</p>
            <p style={{ fontSize: 13, color: C.textMed, marginTop: 4 }}>Try a different category or cadence</p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((p) => {
              const variantLabel = p.variants?.[0]?.l || '';
              const added = inBasket(p.id, variantLabel) || justAdded === (p.id + ':' + variantLabel);
              return (
                <Card key={p.id} style={{ padding: 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <ImgOrEmoji src={null} fallback={p.em} size={56} radius={R.lg} bg="#e6f7fb" />
                    <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                      onClick={() => navigate('product', { product: p, branch: { id: p.branchId, name: p.branchName, rating: p.branchRating } })}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                        <p style={{ fontSize: 14, fontWeight: 800, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); const full = fullBranchFor(p.branchId); if (full) navigate('branch', { branch: full, btype: 'grocery' }); }}
                        style={{ background: 'none', border: 'none', padding: 0, margin: '0 0 4px', cursor: 'pointer', textAlign: 'left', display: 'block', maxWidth: '100%' }}>
                        <p style={{ fontSize: 11, color: C.primary, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          🏬 {p.branchName} · ⭐{p.branchRating} · {p.branchDist} km →
                        </p>
                      </button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: C.primary }}>from {fmt(p.variants?.[0]?.p || 0)}</span>
                        <span style={{ fontSize: 11, color: C.textLight }}>· {p.variants?.[0]?.l}</span>
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); if (!added) handleAdd(p); }}
                      disabled={added}
                      style={{
                        width: 38, height: 38, borderRadius: R.lg,
                        background: added ? C.successBg : C.primary,
                        color: added ? C.success : C.white,
                        border: 'none',
                        fontSize: 18, fontWeight: 800,
                        cursor: added ? 'default' : 'pointer',
                        flexShrink: 0,
                        boxShadow: added ? 'none' : '0 1px 2px rgba(12,192,223,.18), 0 4px 10px rgba(12,192,223,.20)',
                        lineHeight: 1,
                      }}>
                      {added ? '✓' : '+'}
                    </button>
                  </div>
                </Card>
              );
            })}
            {cursor && (
              <button onClick={loadMore} disabled={loadingMore}
                style={{ marginTop: 8, padding: '10px 16px', borderRadius: R.lg, border: `1px solid ${C.border}`, background: C.white, color: C.textMed, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}

        {/* Branch-first alternate entry — for customers who want to browse a specific vendor */}
        {!loading && (
          <div style={{ marginTop: 24, padding: 14, borderRadius: R.xl, background: C.white, border: `1px dashed ${C.border}`, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: C.textMed, marginBottom: 8 }}>Looking for a specific store?</p>
            <button onClick={() => navigate('category-feed', { cat: { id:'grocery', label:'Grocery', em:'🛒', bg:'#dcfce7', live:true, btype:'grocery', filterFn:null, subCats:['All','Dairy','Bakery','Vegetables','Poultry','Grocery'] } })}
              style={{ background: 'none', border: 'none', color: C.primary, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
              🏬 Browse vendors →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
